import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GoogleFontIcon from 'src/icons/GoogleFontIcon';
import { RootState } from 'src/redux/rootReducerType';
import type { Certification } from 'src/types/Certification';
import type { FormSignatureRequestType } from 'src/types/SignatureTypes';
import useAuth from 'src/hooks/useAuth';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import {
  getRequestedDocs,
  getSignedByGroup,
  isUserTenantSignature,
} from './signatureFilters';
import PdfSignViewer from './PdfSignViewer';
import type { PdfSignViewerHandle } from './PdfSignViewer';
import type { RenterSignatureDoc } from './signatureFilters';

const SigningViewPage: FC = () => {
  const { fsrId, docId, certId } = useParams<{
    fsrId?: string;
    docId?: string;
    certId?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const theme = useTheme();
  const { user: authUser } = useAuth();
  const isImpersonated = Boolean(authUser?.impersonator);

  const topBarHeight = useSelector(
    (state: RootState) => state.newRenterUI.topBarHeight
  );
  const bottomNavHeight = useSelector(
    (state: RootState) => state.newRenterUI.bottomNavHeight
  );

  const isSignMode = location.pathname.includes('/signatures/sign');

  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];

  const certificationsLoaded = useSelector(
    (state: RootState) => state.newRenterInterface.certificationsLoaded
  );

  const fsrsLoaded = useSelector(
    (state: RootState) => state.newRenterInterface.fsrsLoaded
  );

  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const userId = user?.id ?? 0;

  const savedSignatureXfdf = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.savedSignatureXfdf
  );
  const hasSavedSig = !!savedSignatureXfdf;

  const docList = useMemo(() => {
    let list: RenterSignatureDoc[];
    if (isSignMode) {
      list = getRequestedDocs(certifications, userId);
    } else {
      const certIdNum = Number(certId);
      const groups = getSignedByGroup(certifications, userId);
      list = Object.values(groups)
        .flatMap((g) => g.docs)
        .filter((d) => d.certificationId === certIdNum);
    }
    return list.sort((a, b) => a.documentTitle.localeCompare(b.documentTitle));
  }, [isSignMode, certifications, userId, certId]);

  const [saving, setSaving] = useState(false);
  const [signError, setSignError] = useState(false);
  const esignRef = useRef<PdfSignViewerHandle | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const activeFsr: FormSignatureRequestType | null = useMemo(() => {
    const targetFsrId = isSignMode ? Number(fsrId) : null;
    if (!targetFsrId) {
      const targetDocId = Number(docId);
      for (const cert of certifications) {
        for (const fsr of cert.form_signatures_request ?? []) {
          if (fsr.certification_document === targetDocId) {
            return fsr;
          }
        }
      }
      return null;
    }

    for (const cert of certifications) {
      for (const fsr of cert.form_signatures_request ?? []) {
        if (fsr.id === targetFsrId) {
          return fsr;
        }
      }
    }
    return null;
  }, [certifications, fsrId, docId, isSignMode]);

  const currentIndex = useMemo(() => {
    if (!activeFsr) {
      return 0;
    }
    const idx = docList.findIndex((d) => d.fsrId === activeFsr.id);
    return idx >= 0 ? idx : 0;
  }, [activeFsr, docList]);

  const navigateToDoc = useCallback(
    (doc: RenterSignatureDoc, replace = false) => {
      if (isSignMode) {
        navigate(`../sign/${doc.fsrId}`, { replace });
      } else {
        navigate(`../view/${doc.certificationId}/doc/${doc.docId}`, {
          replace,
        });
      }
    },
    [isSignMode, navigate]
  );

  const goToPrev = useCallback(() => {
    if (!activeFsr) {
      return;
    }
    const currentIdx = docList.findIndex((d) => d.fsrId === activeFsr.id);
    const prevIdx = currentIdx <= 0 ? docList.length - 1 : currentIdx - 1;
    const prevDoc = docList[prevIdx];
    if (prevDoc) {
      navigateToDoc(prevDoc);
    }
  }, [activeFsr, docList, navigateToDoc]);

  const goToNext = useCallback(() => {
    if (!activeFsr) {
      return;
    }
    const currentIdx = docList.findIndex((d) => d.fsrId === activeFsr.id);
    const nextIdx = currentIdx >= docList.length - 1 ? 0 : currentIdx + 1;
    const nextDoc = docList[nextIdx];
    if (nextDoc) {
      navigateToDoc(nextDoc);
    }
  }, [activeFsr, docList, navigateToDoc]);

  const handleDocSelect = useCallback(
    (e: SelectChangeEvent<number>) => {
      const selectedDoc = docList[Number(e.target.value)];
      if (selectedDoc) {
        navigateToDoc(selectedDoc);
      }
    },
    [docList, navigateToDoc]
  );

  const handleSavingChange = useCallback((val: boolean) => {
    setSaving(val);
  }, []);

  const handleSignError = useCallback(() => {
    setSignError(true);
  }, []);

  useEffect(() => {
    if (!activeFsr || !isSignMode || docList.length === 0) {
      return;
    }

    const isStillInList = docList.some((d) => d.fsrId === activeFsr.id);
    if (!isStillInList && docList.length > 0) {
      const nextDoc = docList[0];
      const nextPath = `../sign/${nextDoc.fsrId}`;
      const suffix = returnTo
        ? `?returnTo=${encodeURIComponent(returnTo)}`
        : '';
      navigate(`${nextPath}${suffix}`, { replace: true });
    }
  }, [activeFsr, isSignMode, docList, returnTo, navigate]);

  const userPendingSigs = useMemo(() => {
    if (!activeFsr) {
      return [];
    }
    return (activeFsr.needed_signatures ?? []).filter(
      (sig) => !sig.signature_completed && isUserTenantSignature(sig, userId)
    );
  }, [activeFsr, userId]);

  const signButtonLabel = 'Sign';

  const handleSignClick = useCallback(() => {
    if (esignRef.current) {
      setSignError(false);
      esignRef.current.signAll(hasSavedSig);
    }
  }, [hasSavedSig]);

  const showSignBar =
    isSignMode && !isImpersonated && !!activeFsr && userPendingSigs.length > 0;

  const [toolbarH, setToolbarH] = useState(0);

  useLayoutEffect(() => {
    setToolbarH(toolbarRef.current?.offsetHeight ?? 0);
  }, [showSignBar]);

  const containerPadding = 12;
  const chromeHeight =
    topBarHeight + bottomNavHeight + toolbarH + containerPadding;
  const viewerHeight = `calc(100vh - ${chromeHeight}px)`;

  if (!certificationsLoaded || !fsrsLoaded) {
    return (
      <Box
        sx={{
          backgroundColor: 'background.default',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (docList.length === 0) {
    return (
      <Box
        sx={{
          backgroundColor: 'background.default',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack alignItems="center" gap={1.5} sx={fadeStyle}>
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
          <Typography variant="body1" color="text.secondary">
            You're all caught up!
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              navigate(returnTo ?? '/renter/signatures');
            }}
          >
            {returnTo ? 'Back to Interview' : 'Return'}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack gap={0} sx={{ ...fadeStyle, flex: 1, overflow: 'hidden' }}>
          {isImpersonated && isSignMode && (
            <Alert severity="info" sx={{ borderRadius: 0 }}>
              Signing is disabled during impersonation. You are viewing in
              read-only mode.
            </Alert>
          )}

          <Box
            sx={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              ref={toolbarRef}
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <IconButton
                  onClick={goToPrev}
                  size="small"
                  disabled={docList.length <= 1}
                  sx={{ flexShrink: 0 }}
                  aria-label="Previous document"
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>

                <Select
                  size="small"
                  value={currentIndex}
                  onChange={handleDocSelect}
                  variant="outlined"
                  sx={{ flex: 1, minWidth: 0 }}
                  inputProps={{ 'aria-label': 'Select document' }}
                >
                  {docList.map((doc, idx) => (
                    <MenuItem key={doc.fsrId} value={idx}>
                      {doc.documentTitle}
                    </MenuItem>
                  ))}
                </Select>

                <IconButton
                  onClick={goToNext}
                  size="small"
                  disabled={docList.length <= 1}
                  sx={{ flexShrink: 0 }}
                  aria-label="Next document"
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            {signError && (
              <Alert
                severity="error"
                sx={{ borderRadius: 0 }}
                onClose={() => {
                  setSignError(false);
                }}
              >
                An error occurred while signing the document. Please try again.
              </Alert>
            )}

            <Box
              sx={{
                height: viewerHeight,
                minHeight: 300,
                position: 'relative',
              }}
            >
              {activeFsr && (
                <PdfSignViewer
                  key={activeFsr.id}
                  ref={esignRef}
                  formSignatureRequest={activeFsr}
                  userId={userId}
                  readOnly={!isSignMode || isImpersonated}
                  onSavingChange={handleSavingChange}
                  onSignError={handleSignError}
                />
              )}

              {showSignBar && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 5,
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    disabled={saving}
                    startIcon={
                      <GoogleFontIcon name="ink_pen" sx={{ fontSize: 20 }} />
                    }
                    onClick={handleSignClick}
                    sx={{
                      boxShadow: theme.shadows[8],
                      borderRadius: 2,
                    }}
                  >
                    <span>{signButtonLabel}</span>
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default SigningViewPage;
